import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

/**
 * Interactive guided tour component
 */
export const GuidedTour = ({
  steps,
  isOpen,
  onClose,
  onComplete,
  storageKey = 'guided_tour_completed'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = steps[currentStep];

  const updateTargetRect = useCallback(() => {
    if (!step?.target) return;
    
    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
      
      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [step]);

  useEffect(() => {
    if (isOpen && step?.target) {
      updateTargetRect();
      window.addEventListener('resize', updateTargetRect);
      window.addEventListener('scroll', updateTargetRect);
      
      return () => {
        window.removeEventListener('resize', updateTargetRect);
        window.removeEventListener('scroll', updateTargetRect);
      };
    }
  }, [isOpen, currentStep, updateTargetRect, step]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(storageKey, 'true');
    onComplete?.();
    onClose();
    setCurrentStep(0);
  };

  const handleSkip = () => {
    localStorage.setItem(storageKey, 'true');
    onClose();
    setCurrentStep(0);
  };

  if (!isOpen) return null;

  const tooltipPosition = step?.position || 'bottom';
  
  const getTooltipStyles = () => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    
    const padding = 16;
    const tooltipWidth = 320;
    
    switch (tooltipPosition) {
      case 'top':
        return {
          bottom: `calc(100% - ${targetRect.top}px + ${padding}px)`,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          top: targetRect.top + targetRect.height + padding,
          left: Math.min(
            Math.max(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, padding),
            window.innerWidth - tooltipWidth - padding
          )
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2,
          right: `calc(100% - ${targetRect.left}px + ${padding}px)`,
          transform: 'translateY(-50%)'
        };
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2,
          left: targetRect.left + targetRect.width + padding,
          transform: 'translateY(-50%)'
        };
      default:
        return {
          top: targetRect.top + targetRect.height + padding,
          left: targetRect.left
        };
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999]"
      >
        {/* Backdrop with hole */}
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 4}
                  y={targetRect.top - 4}
                  width={targetRect.width + 8}
                  height={targetRect.height + 8}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.5)"
            mask="url(#tour-mask)"
          />
        </svg>

        {/* Highlight ring */}
        {targetRect && (
          <motion.div
            className="absolute pointer-events-none border-2 border-[#0047FF] rounded-lg"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8
            }}
            animate={{
              boxShadow: ['0 0 0 0 rgba(0,71,255,0.4)', '0 0 0 8px rgba(0,71,255,0)', '0 0 0 0 rgba(0,71,255,0.4)']
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}

        {/* Tooltip */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={cn(
            'absolute bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-80',
            'border border-[#E5E7EB] dark:border-gray-700'
          )}
          style={getTooltipStyles()}
        >
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-3 right-3 text-[#A1A1AA] hover:text-[#52525B] transition-colors"
            aria-label="Cerrar tour"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Step indicator */}
          <div className="flex gap-1 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i <= currentStep ? 'bg-[#0047FF]' : 'bg-[#E5E7EB] dark:bg-gray-600'
                )}
              />
            ))}
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold mb-2 dark:text-white">{step?.title}</h3>
          <p className="text-sm text-[#52525B] dark:text-gray-300 mb-4">{step?.content}</p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-[#A1A1AA]"
            >
              Saltar tour
            </Button>
            
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="bg-[#0047FF] hover:bg-[#003BCC]"
              >
                {currentStep === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
                {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to manage tour state
 */
export const useTour = (storageKey = 'guided_tour_completed') => {
  const [isOpen, setIsOpen] = useState(false);

  const hasCompletedTour = localStorage.getItem(storageKey) === 'true';

  const startTour = () => setIsOpen(true);
  const closeTour = () => setIsOpen(false);
  const resetTour = () => {
    localStorage.removeItem(storageKey);
  };

  // Auto-start tour for new users
  const autoStart = useCallback(() => {
    if (!hasCompletedTour) {
      setTimeout(() => setIsOpen(true), 1000);
    }
  }, [hasCompletedTour]);

  return {
    isOpen,
    startTour,
    closeTour,
    resetTour,
    autoStart,
    hasCompletedTour
  };
};

export default GuidedTour;
